import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { iconChevronLeft, iconChevronRight } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { displayColour } from './appearance-colour.util';
import {
  APPEARANCE_PRESETS,
  PRESET_PAGE_SIZE,
  PRESET_PAGES,
  presetPage,
  type AppearancePreset,
} from './appearance-presets';
import { IconControl } from './icon-control.component';

const pageOf = (id: string | null) => {
  const index = APPEARANCE_PRESETS.findIndex((preset) => preset.id === id);
  return index < 0 ? 0 : Math.floor(index / PRESET_PAGE_SIZE);
};

/**
 * The preset swatches, nine a page, as one radio group: arrows move and
 * select within the page, wrapping; the arrows either side page through.
 */
export function PresetSwatches({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (preset: AppearancePreset) => void;
}) {
  const [page, setPage] = useState(() => pageOf(selectedId));
  const swatches = useRef<(HTMLButtonElement | null)[]>([]);
  const focusFirst = useRef(false);
  const presets = presetPage(page);
  const checked = presets.findIndex((preset) => preset.id === selectedId);
  const tabbable = checked < 0 ? 0 : checked;

  useEffect(() => {
    if (!focusFirst.current) return;
    focusFirst.current = false;
    swatches.current[0]?.focus();
  }, [page]);

  const turn = (next: number) => {
    focusFirst.current = true;
    setPage(next);
  };

  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const last = presets.length - 1;
    const target =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (target === null) return;
    event.preventDefault();
    onSelect(presets[target]);
    swatches.current[target]?.focus();
  };

  return (
    <div className='flex items-center gap-1'>
      <IconControl
        label='Previous presets'
        icon={iconChevronLeft}
        disabled={page === 0}
        onClick={() => turn(page - 1)}
      />
      <div
        role='radiogroup'
        aria-label='Presets'
        className='flex flex-1 items-center justify-between px-0.5'
      >
        {presets.map((preset, index) => {
          const isChecked = preset.id === selectedId;
          return (
            <Tooltip key={preset.id}>
              <TooltipTrigger asChild>
                <button
                  ref={(node) => {
                    swatches.current[index] = node;
                  }}
                  type='button'
                  role='radio'
                  aria-checked={isChecked}
                  aria-label={preset.name}
                  tabIndex={index === tabbable ? 0 : -1}
                  onClick={() => onSelect(preset)}
                  onKeyDown={(event) => onKeyDown(event, index)}
                  className={cn(
                    'size-5 shrink-0 rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)] transition-shadow',
                    'ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden',
                    isChecked &&
                      'ring-2 ring-foreground ring-offset-2 ring-offset-background'
                  )}
                  style={{
                    backgroundColor: displayColour(
                      preset.hue,
                      preset.tone,
                      preset.intensity
                    ),
                  }}
                />
              </TooltipTrigger>
              <TooltipContent>{preset.name}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
      <IconControl
        label='Next presets'
        icon={iconChevronRight}
        disabled={page >= PRESET_PAGES - 1}
        onClick={() => turn(page + 1)}
      />
    </div>
  );
}
